import type { ComponentProps, VoidComponent } from "solid-js";
import { splitProps, ErrorBoundary, createResource, createEffect, onCleanup, onMount } from "solid-js";
import ApiService from "../../../services/ApiService.ts";
import { APP_BASE_URL } from "../../../constants.ts";
import type CustomerId from "../../../state/CustomerId.ts";
import { debounce, isNil } from "lodash-es";
import {
  PREVIEW_MESSAGE_DATA_SHOW,
  PREVIEW_URL,
  sendClosedPayload,
  sendMappedFormDataPayload,
  sendTokenPayload,
} from "../../../utils/previewUtil.ts";
import type { Form } from "../../../state/form.tsx";
import type { FormVersion } from "../../../state/formVersion.tsx";
import { useModalManager } from "../../../components/modal/ModalManager.tsx";
import GenericFailureModal from "../../GenericFailureModal.tsx";
import { useTranslation } from "../../../state/i18n.tsx";
import { useCustomerState } from "../../../state/customer.tsx";

type FormVersionPreviewProps = {
  ref?: (ref: HTMLIFrameElement) => void;
  form?: Form;
  formVersion?: FormVersion;
} & Omit<ComponentProps<"iframe">, "ref" | "src">;

export const POST_DATA_DEBOUNCE_MS = 250;

const FormVersionPreview: VoidComponent<FormVersionPreviewProps> = (allProps) => {
  const [props, iframeProps] = splitProps(allProps, ["ref", "title", "form", "formVersion"]);

  const { t } = useTranslation();
  const { open } = useModalManager();
  const { customerId } = useCustomerState();

  let iframeRef!: HTMLIFrameElement;

  const [previewToken] = createResource(
    () => props.form?.id,
    (formId) => ApiService.getPreviewToken(formId),
  );

  createEffect(() => {
    if (isNil(previewToken.error)) {
      return;
    }
    open({
      content: () => (
        <GenericFailureModal title={t(($) => $.VERSION_HISTORY_PREVIEW_ERROR_MODAL_TITLE)} error={previewToken.error} />
      ),
    });
  });

  const sendPayloadsDebounced = debounce(
    (window: Window, form: Form, formVersion: FormVersion, customerId: CustomerId, previewToken: string) => {
      sendMappedFormDataPayload(window, form, formVersion, customerId);
      sendTokenPayload(window, previewToken);
    },
    POST_DATA_DEBOUNCE_MS,
    {
      leading: true,
      trailing: true,
    },
  );

  const postDataToIframe = (): void => {
    if (isNil(props.form) || isNil(props.formVersion)) {
      return;
    }

    if (!isNil(previewToken.error)) {
      return;
    }

    const window = iframeRef.contentWindow;
    const resolvedPreviewToken = previewToken();
    if (isNil(window) || isNil(resolvedPreviewToken)) {
      return;
    }
    sendPayloadsDebounced(window, props.form, props.formVersion, customerId, resolvedPreviewToken);
  };

  createEffect(postDataToIframe);

  const handleMessage = (event: MessageEvent): void => {
    if (event.origin === APP_BASE_URL && event.data === PREVIEW_MESSAGE_DATA_SHOW) {
      postDataToIframe();
    }
  };

  onMount(() => {
    window.addEventListener("message", handleMessage);
  });
  onCleanup(() => {
    window.removeEventListener("message", handleMessage);
    sendPayloadsDebounced.cancel();
    sendClosedPayload(iframeRef.contentWindow);
  });

  return (
    <ErrorBoundary fallback={<></>}>
      <iframe
        title={props.title}
        class="size-full"
        ref={(ref) => {
          iframeRef = ref;
          props.ref?.(ref);
        }}
        src={PREVIEW_URL}
        {...iframeProps}
      />
    </ErrorBoundary>
  );
};

export default FormVersionPreview;
